import React, { useEffect, useState } from "react";
import { MapContainer, TileLayer, useMap, Marker, Tooltip, Circle } from "react-leaflet";
import {
    Autocomplete,
    Button,
    Checkbox,
    CircularProgress,
    FormControlLabel,
    Grid,
    MenuItem,
    Switch,
    TextField
} from "@mui/material";
import { GetAllWorkRole, getCompetencies, getWorkforceLocations } from "../../../api/apiCall";
import './WorkforceMap.css';
import { useAuth } from "../../../auth/auth";
import { Controller, useForm } from "react-hook-form";
import L from "leaflet";
import markerIcon from 'leaflet/dist/images/marker-icon.png';
import markerShadow from 'leaflet/dist/images/marker-shadow.png';
import CheckBoxOutlineBlankIcon from '@mui/icons-material/CheckBoxOutlineBlank';
import CheckBoxIcon from '@mui/icons-material/CheckBox';
import { getTierStatus } from "../../../utils/utils";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faCheckCircle, faClock, faEnvelope, faShieldAlt } from "@fortawesome/free-solid-svg-icons";
import { formatDistanceToNow, parseISO } from "date-fns";

const MapController = ({ coordinates, zoom }: { coordinates: [number, number], zoom: number }) => {
    const map = useMap();
    useEffect(() => {
        if (coordinates) {
            map.setView(coordinates, zoom);
        }
    }, [coordinates, map]);
    return null;
};

const customIcon = new L.Icon({
    iconUrl: markerIcon,
    iconRetinaUrl: markerIcon,
    shadowUrl: markerShadow,
    iconSize: [25, 37],
    iconAnchor: [12, 41],
    popupAnchor: [1, -34],
    shadowSize: [41, 41]
});

const icon = <CheckBoxOutlineBlankIcon fontSize="small" />;
const checkedIcon = <CheckBoxIcon fontSize="small" />;

const WorkforceMap = () => {
    const { state } = useAuth();
    const [competencies, setCompetencies] = useState<any>([]);
    const [workers, setWorkers] = useState<any>([]);
    const [location, setLocation] = useState<any>();
    const [coordinates, setCoordinates] = useState<any>([51.505, -0.09]);
    const [zoom, setZoom] = useState<number>(9);
    const [selectedWorker, setSelectedWorker] = useState<any>(null);
    const [workersLocation, setWorkersLocation] = useState<any>(false);
    const [circleRadiusMeters, setCircleRadiusMeters] = useState(0)
    const [loading, setLoading] = useState(false);
    const [selectedCompetencies, setSelectedCompetencies] = useState<any>(null);
    const [workRoles, setWorkRoles] = useState<any>([]);
    const [selectedWorkRoles, setSelectedWorkRoles] = useState<any>(null);

    const { register, handleSubmit, control } = useForm({
        defaultValues: {
            postCode: '',
            distance: 0,
            selectedCompetencyId: "00000000-0000-0000-0000-000000000000",
            selectedExperienceId: "00000000-0000-0000-0000-000000000000",
            tier: null,
            onlyAvailableWorkers: false,
        }
    });

    const getAllCompetencies = async () => {
        try {
            const response = await getCompetencies(state.user?.token);
            setCompetencies(response);
        } catch (error) {
            console.log(error);
        }
    };

    const getWorkRoles = async () => {
        try {
            const response = await GetAllWorkRole(state.user?.token);
            setWorkRoles(response);
        } catch (error) {
            console.log(error);
        }
    };

    const handleDetailsClick = (workerId: any) => {
        const url = `/pace-team/resourcing/profile?workerId=${workerId}`;
        window.open(url, '_blank');
    };

    const onSubmit = async (data: any) => {
        setLoading(true);
        setCircleRadiusMeters(data.distance * 1000);
        const response = await getWorkforceLocations(state.user?.token, {
            postCode: data.postCode,
            distance: data.distance,
            selectedCompetencyIds: selectedCompetencies,
            selectedWorkRoleIds: selectedWorkRoles,
            tier: data.tier,
            onlyAvailableWorkers: data.onlyAvailableWorkers,
        });
        if (response.data.location) {
            setWorkers(response.data.workers);
            setLocation(response.data.location);
            setCoordinates([response?.data.location?.latitude, response?.data.location?.longitude]);
            function getZoomLevelForDistance(km: any) {
                if (km <= 1) return 15;
                if (km <= 5) return 13;
                if (km <= 10) return 12;
                if (km <= 25) return 10;
                if (km <= 50) return 9;
                if (km <= 100) return 8;
                if (km <= 250) return 7;
                if (km <= 500) return 6;
                return 4;
            }
            setZoom(getZoomLevelForDistance(data.distance))
        }
        setLoading(false);
    };

    const getCoordinates = (latitude: any, longitude: any, worker: any) => {
        setCoordinates([latitude, longitude]);
        setZoom(14);
        setSelectedWorker(worker);
    };

    useEffect(() => {
        getAllCompetencies();
        getWorkRoles();
    }, []);

    return (
        <>
            <div className="filter-div">
                <Grid container spacing={1}>
                    <Grid item xs={12} sm={6} md={4} lg={1.6}>
                        <TextField
                            label="Post Code"
                            type="text"
                            size="small"
                            {...register("postCode")}
                            fullWidth
                        />
                    </Grid>
                    <Grid item xs={12} sm={6} md={4} lg={1.6}>
                        <TextField
                            label="Distance in Km"
                            type="number"
                            size="small"
                            {...register("distance")}
                            fullWidth
                        />
                    </Grid>
                    <Grid item xs={12} sm={6} md={4} lg={1.6}>
                        <TextField
                            select
                            fullWidth
                            placeholder="Select Tier"
                            size="small"
                            label="Tier"
                            {...register("tier")}
                            defaultValue={-1}
                        >
                            <MenuItem value={-1}>No Tier</MenuItem>
                            <MenuItem value={0}>Active - Currently resourced on live shift</MenuItem>
                            <MenuItem value={1}>Tier 1 - Not Working / Available {"<"} 24 hours</MenuItem>
                            <MenuItem value={2}>Tier 2 - Working Available {"<"} 7 days</MenuItem>
                            <MenuItem value={3}>Tier 3 - Not looking for work currently</MenuItem>
                            <MenuItem value={4}>Tier 4 - Weekend Worker</MenuItem>
                            <MenuItem value={5}>Tier 5 - Wrong or missing contact details</MenuItem>
                        </TextField>
                    </Grid>
                    <Grid item xs={12} sm={6} md={4} lg={2.6}>
                        <Autocomplete
                            multiple
                            size="small"
                            options={workRoles}
                            disableCloseOnSelect
                            onChange={(_, newValue) => {
                                const workRolesSelected = newValue.map(item => item.id);
                                setSelectedWorkRoles(workRolesSelected);
                            }}
                            getOptionLabel={(option: any) => option.workRoleName}
                            renderOption={(props, option, { selected }) => {
                                const { key, ...optionProps }: any = props;
                                return (
                                    <li key={key} {...optionProps}>
                                        <Checkbox
                                          icon={icon}
                                          checkedIcon={checkedIcon}
                                          style={{ marginRight: 8 }}
                                          checked={selected}
                                        />
                                        {option.workRoleName}
                                    </li>
                                );
                            }}
                            renderInput={(params) => (
                                <TextField {...params} label="Work Roles" />
                            )}
                            sx={{
                                '& .MuiAutocomplete-inputRoot': {
                                    flexWrap: 'nowrap',
                                    overflowX: 'auto',
                                    scrollbarWidth: 'thin',
                                    '&::-webkit-scrollbar': {
                                      height: '9px',
                                    },
                                },
                                '& .MuiChip-root': {
                                    marginRight: '4px',
                                    maxWidth: '150px',
                                    overflow: 'hidden',
                                },
                            }}
                        />
                    </Grid>
                     <Grid item xs={12} sm={6} md={4} lg={2.6}>
                        <Autocomplete
                            multiple
                            size="small"
                            options={competencies}
                            disableCloseOnSelect
                            onChange={(_, newValue) => {
                                const competenciesSelected = newValue.map(item => item.id);
                                setSelectedCompetencies(competenciesSelected);
                            }}
                            getOptionLabel={(option: any) => option.skillName}
                            renderOption={(props, option, { selected }) => {
                                const { key, ...optionProps }: any = props;
                                return (
                                    <li key={key} {...optionProps}>
                                        <Checkbox
                                          icon={icon}
                                          checkedIcon={checkedIcon}
                                          style={{ marginRight: 8 }}
                                          checked={selected}
                                        />
                                        {option.skillName}
                                    </li>
                                );
                            }}
                            renderInput={(params) => (
                                <TextField {...params} label="Competencies" />
                            )}
                            sx={{
                                '& .MuiAutocomplete-inputRoot': {
                                    flexWrap: 'nowrap',
                                    overflowX: 'auto',
                                    scrollbarWidth: 'thin',
                                    '&::-webkit-scrollbar': {
                                      height: '9px',
                                    },
                                },
                                '& .MuiChip-root': {
                                    marginRight: '4px',
                                    maxWidth: '150px',
                                    overflow: 'hidden',
                                },
                            }}
                        />
                    </Grid>
                    <Grid item xs={12} sm={6} md={4} lg={1.5}>
                        <Button onClick={handleSubmit(onSubmit)} disabled={loading} style={{boxShadow: 'none'}} fullWidth variant="contained">
                            {loading ? <CircularProgress size={25} style={{color: 'white'}} /> : 'Filter'}
                        </Button>
                    </Grid>
                </Grid>
            </div>

            <div className="main-map-div">
                <div className="map-div">
                    <MapContainer center={coordinates} zoom={zoom} style={{ height: '74vh', width: '70vw', zIndex: 1 }}>
                        <TileLayer url="https://mt1.google.com/vt/lyrs=m&x={x}&y={y}&z={z}" />
                        {selectedWorker && (
                            <Marker position={coordinates} icon={customIcon}>
                                <Tooltip offset={[0, -30]} permanent={true} direction="top">
                                    <div style={{ backgroundColor: 'white', fontWeight: 600, textAlign: 'center' }}>
                                        {selectedWorker.fullName} <br /> {selectedWorker.pts}
                                    </div>
                                </Tooltip>
                            </Marker>
                        )}
                        {workersLocation && (
                            <>
                                {workers.map((worker: any) => (
                                    <Marker
                                        key={worker.id}
                                        icon={customIcon}
                                        position={[worker.latitude, worker.longitude]}
                                    >
                                        <Tooltip offset={[0, -30]} permanent={true} direction="top">
                                            <div style={{ backgroundColor: 'white', fontWeight: 600, textAlign: 'center' }}>
                                                {worker.fullName} <br /> {worker.pts}
                                            </div>
                                        </Tooltip>
                                    </Marker>
                                ))}
                            </>
                        )}
                        {location &&
                            <>
                                <Marker position={[location.latitude, location.longitude]} icon={customIcon}>
                                    <Tooltip offset={[0, -30]} permanent direction="top" opacity={1} className="tooltip-red">
                                        {location.postCode}
                                    </Tooltip>
                                </Marker>
                                <Circle
                                    center={[location.latitude, location.longitude]}
                                    radius={circleRadiusMeters}
                                    pathOptions={{
                                        color: '#F69E5E',
                                        fillColor: '#021332',
                                        fillOpacity: 0.2,
                                    }}
                                />
                            </>
                        }
                        <MapController coordinates={coordinates} zoom={zoom} />
                    </MapContainer>
                </div>

                <div className="list-workers">
                    <div className="container-2">
                        <h1 className="workers-title">
                            List of workers - total <span className="total">{workers.length}</span>
                            {workers.length > 0 && (
                                <>
                                    {!workersLocation ?
                                        <Button
                                            onClick={() => setWorkersLocation(true)}
                                            style={{margin: 5, marginRight: 0}}
                                        >
                                            Show all
                                        </Button> :
                                        <Button
                                            onClick={() => setWorkersLocation(false)}
                                            style={{margin: 5, marginRight: 0}}
                                        >
                                            Clear All
                                        </Button>
                                    }
                                </>
                            )}
                            <Controller
                                name="onlyAvailableWorkers"
                                control={control}
                                render={({ field }) => (
                                    <FormControlLabel
                                        style={{marginLeft: 1}}
                                        label=""
                                        control={
                                            <Switch
                                                checked={field.value}
                                                onChange={(e) => field.onChange(e.target.checked)}
                                            />
                                        }
                                    />
                                )}
                            />
                        </h1>
                        <div className="workers-list">
                            {workers.map((worker: any, index: any) => (
                                <div key={index} className="worker-card"
                                    onClick={() => getCoordinates(worker.latitude, worker.longitude, worker)}
                                >
                                    <div className="worker-card-content">
                                        <div className="worker-main-info">
                                            <div className="header-worker">
                                                <div>
                                                    <h2 className="worker-name" onClick={() => handleDetailsClick(worker.id)}>{worker.fullName}</h2>
                                                    {worker.scoreData?.latestJobDate ? (
                                                      <p style={{ marginTop: 0, marginBottom: 0, color: "gray", fontSize: 13 }}>
                                                        {formatDistanceToNow(
                                                          parseISO(worker.scoreData.latestJobDate),
                                                          { addSuffix: true }
                                                        )}
                                                      </p>
                                                    ) : (
                                                        <p style={{ marginTop: 0, marginBottom: 0, color: "gray", fontSize: 13 }}>
                                                            No Work yet
                                                        </p>
                                                    )}
                                                </div>
                                                <span className="worker-tier-badge">
                                                    {getTierStatus(worker.tier)}
                                                </span>
                                            </div>
                                            <div className="worker-details-grid">
                                                <div className="detail-item" style={{ alignItems: "start" }}>
                                                    <span className="detail-label">PTS</span>
                                                    <span className="detail-value">{worker.pts}</span>
                                                </div>
                                                <div className="detail-item"  style={{ alignItems: "center" }}>
                                                    <span className="detail-label">Phone Number</span>
                                                    <span className="detail-value">{worker.phoneNumber}</span>
                                                </div>
                                                <div className="detail-item"  style={{ alignItems: "end" }}>
                                                    <span className="detail-label">Post Code</span>
                                                    <span className="detail-value">{worker.postCode}</span>
                                                </div>
                                            </div>
                                        </div>
                                        <div className="score-indicators">
                                            <div className="score-grid">
                                                <div 
                                                    className="score-card reliability"
                                                >
                                                    <div className="score-icon">
                                                        <FontAwesomeIcon icon={faShieldAlt} />
                                                    </div>
                                                    <div className="score-content">
                                                        <span className="score-label">Reliability</span>
                                                        <span className="score-value">
                                                            {worker.scoreData.reliabilityScore === -1 
                                                            ? "N/A" 
                                                            : `${worker.scoreData.reliabilityScore}%`
                                                            }
                                                        </span>
                                                    </div>
                                                </div>
                                                <div className="score-card safety">
                                                    <div className="score-icon">
                                                        <FontAwesomeIcon icon={faCheckCircle} />
                                                    </div>
                                                    <div className="score-content">
                                                        <span className="score-label">Safety</span>
                                                        <span className="score-value">
                                                            {worker.scoreData.safetyScore === -1 
                                                            ? "N/A" 
                                                            : `${worker.scoreData.safetyScore}%`
                                                            }
                                                        </span>
                                                    </div>
                                                </div>
                                                <div className="score-card punctuality">
                                                    <div className="score-icon">
                                                        <FontAwesomeIcon icon={faClock} />
                                                    </div>
                                                    <div className="score-content">
                                                        <span className="score-label">Punctuality</span>
                                                        <span className="score-value">
                                                            {worker.scoreData.punctualityScore === -1 
                                                            ? "N/A" 
                                                            : `${worker.scoreData.punctualityScore}%`
                                                            }
                                                        </span>
                                                    </div>
                                                </div>
                                                <div className="score-card response">
                                                    <div className="score-icon">
                                                        <FontAwesomeIcon icon={faEnvelope} />
                                                    </div>
                                                    <div className="score-content">
                                                        <span className="score-label">Response Rate</span>
                                                        <span className="score-value">
                                                            {worker.scoreData.responseScore === -1 
                                                            ? "N/A" 
                                                            : `${worker.scoreData.responseScoreDetails}`
                                                            }
                                                        </span>
                                                    </div>
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            ))}
                        </div>
                    </div>
                </div>
            </div>
        </>
    );
};

export default WorkforceMap;
